/**
 * Canonical tile & scorecard catalog for the Executive Command Center.
 * Org admins can enable/disable tiles and attach external data providers.
 * See EnterpriseExecutiveDashboardPRD.md §6.1 — Practice Analytics Hub.
 */

export const TILE_PROVIDER_TYPES = {
  RCM_NATIVE: 'rcm_native',
  INTEGRATION: 'integration',
  CUSTOM_API: 'custom_api',
  MANUAL: 'manual',
  CSV_FEED: 'csv_feed',
};

/** Where a tile may appear on the executive surface */
export const TILE_PLACEMENTS = {
  TOP_KPI: 'top_kpi',
  SCORECARD_METRIC: 'scorecard_metric',
  BENCHMARK: 'benchmark',
  TREND_SERIES: 'trend_series',
  OPERATIONAL_HIGHLIGHT: 'operational_highlight',
};

/**
 * Default enterprise executive tile catalog.
 * Org admins can enable/disable and reorder tiles via dashboard layout config.
 * `provider` describes how data is resolved — not all providers are live in Milestone A.
 */
export const DEFAULT_EXECUTIVE_TILE_CATALOG = {
  topKpis: [
    {
      id: 'net_revenue',
      label: 'Net Revenue',
      unit: 'currency',
      provider: TILE_PROVIDER_TYPES.INTEGRATION,
      integrationKey: 'general_ledger',
      polarity: 'higher_is_better',
      sortOrder: 1,
      enabled: true,
    },
    {
      id: 'operating_margin',
      label: 'Operating Margin',
      unit: 'percent',
      provider: TILE_PROVIDER_TYPES.INTEGRATION,
      integrationKey: 'general_ledger',
      polarity: 'higher_is_better',
      sortOrder: 2,
      enabled: true,
    },
    {
      id: 'encounters',
      label: 'Encounters',
      unit: 'count',
      provider: TILE_PROVIDER_TYPES.INTEGRATION,
      integrationKey: 'ehr_scheduling',
      polarity: 'higher_is_better',
      sortOrder: 3,
      enabled: true,
    },
    {
      id: 'net_collection_rate',
      label: 'Net Collection Rate',
      unit: 'percent',
      provider: TILE_PROVIDER_TYPES.RCM_NATIVE,
      polarity: 'higher_is_better',
      sortOrder: 4,
      enabled: true,
    },
    {
      id: 'days_in_ar',
      label: 'Days in A/R',
      unit: 'days',
      provider: TILE_PROVIDER_TYPES.RCM_NATIVE,
      polarity: 'lower_is_better',
      sortOrder: 5,
      enabled: true,
    },
    {
      id: 'denial_rate',
      label: 'Denial Rate',
      unit: 'percent',
      provider: TILE_PROVIDER_TYPES.RCM_NATIVE,
      polarity: 'lower_is_better',
      sortOrder: 6,
      enabled: true,
    },
    {
      id: 'observed_capture',
      label: 'Observed capture',
      unit: 'percent',
      provider: TILE_PROVIDER_TYPES.RCM_NATIVE,
      polarity: 'higher_is_better',
      sortOrder: 7,
      enabled: true,
    },
    {
      id: 'ehr_coverage',
      label: 'EHR coverage',
      unit: 'percent',
      provider: TILE_PROVIDER_TYPES.RCM_NATIVE,
      polarity: 'higher_is_better',
      sortOrder: 8,
      enabled: true,
    },
    {
      id: 'remit_coverage',
      label: 'Remit coverage',
      unit: 'percent',
      provider: TILE_PROVIDER_TYPES.RCM_NATIVE,
      polarity: 'higher_is_better',
      sortOrder: 9,
      enabled: true,
    },
    {
      id: 'cash_on_hand',
      label: 'Cash on Hand',
      unit: 'days',
      provider: TILE_PROVIDER_TYPES.INTEGRATION,
      integrationKey: 'treasury',
      polarity: 'higher_is_better',
      sortOrder: 10,
      enabled: true,
    },
    {
      id: 'patient_retention',
      label: 'Patient Retention',
      unit: 'percent',
      provider: TILE_PROVIDER_TYPES.INTEGRATION,
      integrationKey: 'patient_registry',
      polarity: 'higher_is_better',
      sortOrder: 11,
      enabled: true,
    },
  ],
  scorecards: [
    {
      id: 'revenue_cycle',
      title: 'Revenue Cycle (CRO)',
      provider: TILE_PROVIDER_TYPES.RCM_NATIVE,
      enabled: true,
      sortOrder: 1,
    },
    {
      id: 'cfo',
      title: 'CFO / Finance',
      provider: TILE_PROVIDER_TYPES.INTEGRATION,
      integrationKey: 'general_ledger',
      enabled: true,
      sortOrder: 2,
    },
    {
      id: 'ceo',
      title: 'CEO / Growth',
      provider: TILE_PROVIDER_TYPES.INTEGRATION,
      integrationKey: 'ehr_pm',
      enabled: true,
      sortOrder: 3,
    },
    {
      id: 'coo',
      title: 'COO / Operations',
      provider: TILE_PROVIDER_TYPES.INTEGRATION,
      integrationKey: 'ehr_scheduling',
      enabled: true,
      sortOrder: 4,
    },
    {
      id: 'cmo',
      title: 'CMO / Medical',
      provider: TILE_PROVIDER_TYPES.INTEGRATION,
      integrationKey: 'ehr_clinical',
      enabled: true,
      sortOrder: 5,
    },
    {
      id: 'dental',
      title: 'Dental Services',
      provider: TILE_PROVIDER_TYPES.INTEGRATION,
      integrationKey: 'dental_module',
      enabled: true,
      sortOrder: 6,
    },
    {
      id: 'hr',
      title: 'HR / Workforce',
      provider: TILE_PROVIDER_TYPES.INTEGRATION,
      integrationKey: 'hris',
      enabled: true,
      sortOrder: 7,
    },
    {
      id: 'pharmacy',
      title: 'Pharmacy',
      provider: TILE_PROVIDER_TYPES.INTEGRATION,
      integrationKey: 'pharmacy',
      enabled: true,
      sortOrder: 8,
    },
    {
      id: 'behavioral_health',
      title: 'Behavioral Health',
      provider: TILE_PROVIDER_TYPES.INTEGRATION,
      integrationKey: 'behavioral_health',
      enabled: true,
      sortOrder: 9,
    },
  ],
};

function applySortOrder(items, orderIds, idKey = 'id') {
  if (!orderIds?.length) {
    return [...items].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }
  const byId = new Map(items.map((item) => [item[idKey], item]));
  const ordered = orderIds.map((id) => byId.get(id)).filter(Boolean);
  const remaining = items
    .filter((item) => !orderIds.includes(item[idKey]))
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  return [...ordered, ...remaining];
}

export function getEnabledCatalog(orgLayoutOverrides = {}) {
  const disabledTop = new Set(orgLayoutOverrides.disabledTopKpiIds || []);
  const disabledScorecards = new Set(orgLayoutOverrides.disabledScorecardIds || []);

  const topKpis = DEFAULT_EXECUTIVE_TILE_CATALOG.topKpis.filter(
    (t) => t.enabled && !disabledTop.has(t.id)
  );
  const scorecards = DEFAULT_EXECUTIVE_TILE_CATALOG.scorecards.filter(
    (s) => s.enabled && !disabledScorecards.has(s.id)
  );

  return {
    topKpis: applySortOrder(topKpis, orgLayoutOverrides.topKpiOrder),
    scorecards: applySortOrder(scorecards, orgLayoutOverrides.scorecardOrder),
  };
}

/** Stable default order ids for settings layout editors (must not be recreated per render). */
export const DEFAULT_EXECUTIVE_TOP_KPI_ORDER = DEFAULT_EXECUTIVE_TILE_CATALOG.topKpis.map((t) => t.id);
export const DEFAULT_EXECUTIVE_SCORECARD_ORDER = DEFAULT_EXECUTIVE_TILE_CATALOG.scorecards.map((s) => s.id);
